import React, { useMemo, useCallback } from 'react'
import {useTable, ColumnInstance, Row} from 'react-table'
import {CustomHeaderColumn} from '../table/columns/CustomHeaderColumn'
import {CustomRow} from '../table/columns/CustomRow'
import {useQueryResponseData, useQueryResponseLoading, useQueryResponse} from '../core/QueryResponseProvider'
import { useSelector } from 'react-redux'
import {getUsersColumns} from './columns/_columns'
import {User} from '../core/_models'
import {UsersListLoading} from '../components/loading/UsersListLoading'
import {UsersListPagination} from '../components/pagination/UsersListPagination'
import {KTCardBody} from '../../../../../_metronic/helpers'
import { getUsers, exportUsers } from '../core/_requests'
import {getAccountLevelDisplay} from '../../../../../Utils/accountLevelUtils'

type UsersTableProps = {
  userType?: string
  customerCode?: number
}

const UsersTable = ({userType, customerCode}: UsersTableProps) => {
  const users = useQueryResponseData()
  const isLoading = useQueryResponseLoading()
  const { query } = useQueryResponse() || {}
  // Get filter value from Redux
  const filterValue = useSelector((state: any) => {
    if (userType === 'CONSUMER' && customerCode === 0) {
      return state.table.RequesterManagement?.[0]?.filterData
    } else if (userType === 'INTERPRETER') {
      return state.table.InterperterManagement?.[0]?.filterData
    } else if (userType === 'SYSTEM') {
      return state.table.AdministratorManagement?.[0]?.filterData
    } else {
      return state.table.CompanyUserManagement?.[0]?.filterData
    }
  })
  const data = useMemo(() => {
    if (isLoading) {
      return []
    } else {
      return users
    }
  }, [users, isLoading])

  const columns = useMemo(() => getUsersColumns(userType), [userType])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data,
  })

  // Export to Excel logic
  // Export columns and mapping for each userType
  let exportHeader: string[] = []
  let flattenData: (exportData: any[]) => Record<string, any>[] = () => []
  if (userType === 'INTERPRETER') {
    exportHeader = [
      'User ID', 'First Name', 'Last Name', 'Email', 'Phone Number', 'Address', 'Country', 'City', 'State', 'Zipcode', 'Latitude', 'Longitude',
      'Account ID', 'Account Name', 'Office Location', 'Role', 'Status', 'Service Types', 'Languages', 'Joined Date', 'Last Assignment', 'Last Login',
      'Scheduled Telephonic', 'On-Demand Telephonic', 'On-Demand Video Interpreting', 'Scheduled Video Interpreting',
      'Onsite Consecutive', 'Onsite Simultaneous', 'Third Party Platform'
    ]
    flattenData = (exportData: any[]) => exportData.map((user: any) => ({
      'User ID': user.userId || '',
      'First Name': user.firstName || '',
      'Last Name': user.lastName || '',
      'Email': user.email || '',
      'Phone Number': user.phoneNumber || '',
      'Address': user.address || '',
      'Country': user.country || user.contryName || '',
      'City': user.city || '',
      'State': user.state || '',
      'Zipcode': user.postalCode || '',
      'Latitude': user.latitude || '',
      'Longitude': user.longitude || '',
      'Account ID': user.fK_Customer || '',
      'Account Name': user.customerName || '',
      'Office Location': user.officeLocation || '',
      'Role': user.role || '',
      'Status': (user.email && user.isdeleted) || user.accountStatus === 'Not Active'
        ? 'Inactive'
        : user.isConfirmed
        ? 'Active'
        : 'Pending',
      'Service Types': Array.isArray(user.serviceTypes) ? user.serviceTypes.join(', ') : (user.accountServiceTypes || ''),
      'Languages': Array.isArray(user.userLanguages)
        ? user.userLanguages.map((l: { languageTo: { name: any } }) => l.languageTo?.name || '').filter(Boolean).join('; ')
        : '',
      'Joined Date': user.join_date || '',
      //'Last Assignment': user.lastAssignment || '',
      'Last Login': user.lastLoginDate || '',
      'Scheduled Telephonic': user.opI_ShdTelephonic ? 'Yes' : 'No',
      'On-Demand Telephonic': user.opI_OndemandTelephonic ? 'Yes' : 'No',
      'On-Demand Video Interpreting': user.vrI_OndemandVideoInteroreting ? 'Yes' : 'No',
      'Scheduled Video Interpreting': user.vrI_ShdVideoInteroreting ? 'Yes' : 'No',
      'Onsite Consecutive': user.osI_OnsiteConsecutive ? 'Yes' : 'No',
      'Onsite Simultaneous': user.osI_OnsiteSimultaneous ? 'Yes' : 'No',
      'Third Party Platform': user.other_3rdPartyPlatform ? 'Yes' : 'No',
    }))
  } else if (userType === 'CONSUMER') {
    exportHeader = [
      'Requester ID', 'First Name', 'Last Name', 'Email', 'Phone Number', 'Address', 'Country', 'City', 'State', 'Zipcode',
      'Account ID', 'Account Name', 'Office Location', 'Default Service Type(OPI)', 'Other Service Types', 'Pre Call Policy', 'IVR Pin',
      'Role', 'Status', 'Account Level', 'Joined Date', 'Last Assignment', 'Last Login',
      'Scheduled Telephonic', 'On-Demand Telephonic', 'On-Demand Video Interpreting', 'Scheduled Video Interpreting',
      'Onsite Consecutive', 'Onsite Simultaneous', 'Third Party Platform'
    ]
    flattenData = (exportData: any[]) => exportData.map((user: any) => ({
      'Requester ID': user.code || '',
      'First Name': user.firstName || '',
      'Last Name': user.lastName || '',
      'Email': user.email || '',
      'Phone Number': user.phoneNumber || '',
      'Address': user.address || '',
      'Country': user.country || user.contryName || '',
      'City': user.city || '',
      'State': user.state || '',
      'Zipcode': user.postalCode || '',
      'Account ID': user.fK_Customer || '',
      'Account Name': user.customerName || '',
      'Office Location': user.officeLocation || '',
      'Default Service Type(OPI)': user.serviceType || '',
      'Other Service Types': Array.isArray(user.serviceTypes) ? user.serviceTypes.join(', ') : (user.accountServiceTypes || ''),
      'Pre Call Policy': user.fK_PreCallPolicy || '',
      'IVR Pin': user.pinCodeString || user.pinCode || '',
      'Role': user.role || '',
      'Status': (user.email && user.isdeleted) || user.accountStatus === 'Not Active'
        ? 'Inactive'
        : user.isConfirmed
        ? 'Active'
        : 'Pending',
      'Account Level': getAccountLevelDisplay(user.accountLevel)?.label || '',
      'Joined Date': user.join_date || '',
      //'Last Assignment': user.lastAssignment || '',
      'Last Login': user.lastLoginDate || '',
      'Scheduled Telephonic': user.opI_ShdTelephonic ? 'Yes' : 'No',
      'On-Demand Telephonic': user.opI_OndemandTelephonic ? 'Yes' : 'No',
      'On-Demand Video Interpreting': user.vrI_OndemandVideoInteroreting ? 'Yes' : 'No',
      'Scheduled Video Interpreting': user.vrI_ShdVideoInteroreting ? 'Yes' : 'No',
      'Onsite Consecutive': user.osI_OnsiteConsecutive ? 'Yes' : 'No',
      'Onsite Simultaneous': user.osI_OnsiteSimultaneous ? 'Yes' : 'No',
      'Third Party Platform': user.other_3rdPartyPlatform ? 'Yes' : 'No',
    }))
  } else if (userType === 'SYSTEM') {
    exportHeader = [
      'User ID', 'First Name', 'Last Name', 'Email', 'Phone Number', 'Address', 'Country', 'City', 'State', 'Zipcode',
      'Account ID', 'Account Name', 'Office Location', 'Role', 'Status', 'Joined Date', 'Last Login'
    ]
    flattenData = (exportData: any[]) => exportData.map((user: any) => ({
      'User ID': user.code || '',
      'First Name': user.firstName || '',
      'Last Name': user.lastName || '',
      'Email': user.email || '',
      'Phone Number': user.phoneNumber || '',
      'Address': user.address || '',
      'Country': user.country || user.contryName || '',
      'City': user.city || '',
      'State': user.state || '',
      'Zipcode': user.postalCode || '',
      'Account ID': user.fK_Customer || '',
      'Account Name': user.customerName || '',
      'Office Location': user.officeLocation || '',
      'Role': user.role || '',
      'Status': (user.email && user.isdeleted) || user.accountStatus === 'Not Active'
        ? 'Inactive'
        : user.isConfirmed
        ? 'Active'
        : 'Pending',
      'Joined Date': user.join_date || '',
      'Last Login': user.lastLoginDate || '',
    }))
  }

  // CSV generator
  const generateCSV = (header: string[], rows: Record<string, any>[]) => {
    const escape = (val: any) => {
      if (val == null) return ''
      const str = String(val)
      // Escape quotes and commas
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"'
      }
      return str
    }
    const csvRows = [header.join(',')]
    for (const row of rows) {
      csvRows.push(header.map(h => escape(row[h])).join(','))
    }
    return csvRows.join('\r\n')
  }

  // Detect if filter is applied
  const isFilterApplied = useMemo(() => {
    if (!filterValue) return false
    return Object.values(filterValue).some(
      (v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
    )
  }, [filterValue])

  // Export handler
  const handleExport = useCallback(async () => {
    let exportData = data
    
    if (isFilterApplied) {
      // Fetch all filtered data from backend (no pagination)
      try {
        const response = await exportUsers("page=1&items_per_page=99999", userType || '', customerCode || 0, filterValue)
        exportData = response?.data || []
      } catch (err) {
        exportData = data
      }
    }else{
       try {
        const response = await exportUsers(query, userType || '', customerCode || 0, filterValue)
        exportData = response?.data || []
      } catch (err) {
        exportData = data
      }
    }
    const body = flattenData(exportData)
    const csv = generateCSV(exportHeader, body)
    // Download CSV with UTF-8 BOM to ensure Excel correctly interprets special characters
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    let exportFileName = 'Users_List.csv';
    if (userType === 'CONSUMER') {
      exportFileName = 'Requesters_List.csv';
    } else if (userType === 'INTERPRETER') {
      exportFileName = 'Interpreters_List.csv';
    } else if (userType === 'SYSTEM') {
      exportFileName = 'Administrators_List.csv';
    }
    link.download = exportFileName;
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data, isFilterApplied, query, userType, customerCode, filterValue])

  React.useEffect(() => {
    const handler = () => {
      handleExport()
    }
    window.addEventListener('export-users-table', handler)
    return () => window.removeEventListener('export-users-table', handler)
  }, [handleExport])

  return (
    <KTCardBody className='py-4 px-0'>
      <div className='table-responsive'>
        <table
          id='kt_table_users'
          className='table table-row-dashed table-row-gray-300 align-middle gs-2 gy-2 table-hover'
          {...getTableProps()}
          data-testid='users-table'
        >
          <thead>
            <tr className='text-start text-muted fw-semibold fs-7 text-uppercase gs-0'>
              {headers.map((column: ColumnInstance<User>) => (
                <CustomHeaderColumn key={column.id} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className='table-hover' {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row: Row<User>, i) => {
                prepareRow(row)
                return <CustomRow row={row} key={row.id} />
              })
            ) : (
              <tr>
                <td colSpan={8}>
                  <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                    <div className='text-center'>
                      <div className='symbol symbol-200px '>
                        <img src='/media/other/nodata.png' alt='' />
                      </div>
                    </div>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                      No matching records found
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <UsersListPagination userType={userType || ''} customerCode={customerCode} />
      {isLoading && <UsersListLoading />}
    </KTCardBody>
  )
}

export {UsersTable}
